import { useEffect, useState } from "react";
import { api, formatApiError, RESOURCE_CATEGORIES } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Filter, Mail, Phone, Building2, ShieldCheck, X, ExternalLink, CheckCircle2, XCircle, MessageCircle, Archive, Loader2 } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "",                  label: "All" },
  { value: "pending_review",    label: "Pending review" },
  { value: "needs_more_info",   label: "Needs more info" },
  { value: "approved",          label: "Approved" },
  { value: "rejected",          label: "Rejected" },
  { value: "archived",          label: "Archived" },
];

const STATUS_STYLES = {
  pending_review:   "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  needs_more_info:  "bg-[#DBEAFE] text-[#1E3A8A] border-[#BFDBFE]",
  approved:         "bg-[#DCFCE7] text-[#166534] border-[#A7F3D0]",
  rejected:         "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  archived:         "bg-[#F1F5F9] text-[#334155] border-[#E5E5E0]",
};

export default function PartnerSubmissions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      const { data } = await api.get("/admin/partner-submissions", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  return (
    <div className="space-y-6" data-testid="partner-submissions-page">
      <div>
        <div className="hw-eyebrow mb-2">Resource Directory · admin queue</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Partner Resource Submissions</h1>
        <p className="text-sm text-[#334155] mt-1 max-w-3xl">
          Organizations applying to be listed in the HarborWell Resource Directory use the Partner Resource
          Application at{" "}
          <a
            href="/partner/apply"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open the Partner Resource Directory application in a new tab"
            data-testid="link-resource-directory-apply"
            className="font-mono text-[#1E3A8A] hover:underline"
          >
            /partner/apply
          </a>
          . Submissions from the Resource Directory form appear here for review and are not
          published to the live directory until approved.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="partner-submissions-filter">
        <Filter className="w-4 h-4 text-[#334155]" />
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value || "all"}
            onClick={() => setStatus(s.value)}
            data-testid={`filter-${s.value || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${status === s.value ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#0F172A] border-[#E5E5E0] hover:bg-[#F1F5F9]"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <Inbox className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No submissions {status ? `in "${status.replace(/_/g, " ")}"` : "yet"}</h3>
          <p className="text-sm text-[#334155]">Once organizations submit via the public form, you&apos;ll see them here.</p>
        </div>
      ) : (
        <ul className="bg-white border border-[#E5E5E0] rounded-2xl divide-y divide-[#F0EFEB]" data-testid="partner-submissions-list">
          {items.map((s) => (
            <li key={s.id} className="p-4 hover:bg-[#F9F8F6] transition-colors cursor-pointer" onClick={() => setActive(s)} data-testid={`submission-row-${s.id}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-semibold text-[#0F172A]">{s.organization_name}</span>
                    <StatusChip status={s.status} />
                  </div>
                  <div className="mt-1 text-xs text-[#334155] flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {s.primary_contact_name}{s.primary_contact_title ? ` · ${s.primary_contact_title}` : ""}</span>
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {s.primary_contact_email}</span>
                    {s.primary_contact_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {s.primary_contact_phone}</span>}
                  </div>
                  {s.selected_categories?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.selected_categories.slice(0, 6).map((c, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[10px] text-[#1E3A8A] border border-[#E5E5E0]">
                          {c.label}{c.slug ? <span className="font-mono opacity-60"> · {c.slug}</span> : ""}
                        </span>
                      ))}
                      {s.selected_categories.length > 6 && <span className="text-[10px] text-[#334155]">+{s.selected_categories.length - 6}</span>}
                    </div>
                  )}
                </div>
                <div className="text-right text-[10px] text-[#334155] whitespace-nowrap">
                  Submitted<br />
                  {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {active && <DetailModal submission={active} onClose={() => setActive(null)} onChanged={load} />}
    </div>
  );
}

function StatusChip({ status }) {
  const css = STATUS_STYLES[status] || STATUS_STYLES.archived;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${css}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

function DetailModal({ submission: s, onClose, onChanged }) {
  const [action, setAction] = useState(null); // 'approve' | 'reject' | 'request-info' | 'archive'
  const isTerminal = ["approved", "rejected", "archived"].includes(s.status);
  const isPending = s.status === "pending_review" || s.status === "needs_more_info";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="submission-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-[#E5E5E0] flex items-start justify-between z-10">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-xl font-semibold text-[#0F172A]">{s.organization_name}</h3>
              <StatusChip status={s.status} />
            </div>
            <p className="text-xs text-[#334155] mt-1">
              Submitted {s.created_at ? new Date(s.created_at).toLocaleString() : ""}{s.submitter_ip ? ` · IP ${s.submitter_ip}` : ""}
              {s.reviewed_by_name ? ` · last reviewed by ${s.reviewed_by_name}` : ""}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F1F5F9]" data-testid="submission-detail-close">
            <X className="w-5 h-5 text-[#334155]" />
          </button>
        </div>

        {/* Decision toolbar */}
        <div className="px-6 py-3 bg-[#F9F8F6] border-b border-[#E5E5E0] flex flex-wrap gap-2">
          {isPending && (
            <>
              <ToolbarBtn icon={<CheckCircle2 className="w-4 h-4" />} onClick={() => setAction("approve")} testId="action-approve" tone="primary">
                Approve &amp; create resource
              </ToolbarBtn>
              <ToolbarBtn icon={<MessageCircle className="w-4 h-4" />} onClick={() => setAction("request-info")} testId="action-request-info">
                Request more info
              </ToolbarBtn>
              <ToolbarBtn icon={<XCircle className="w-4 h-4" />} onClick={() => setAction("reject")} testId="action-reject" tone="danger">
                Reject
              </ToolbarBtn>
            </>
          )}
          {isTerminal && s.status !== "archived" && (
            <ToolbarBtn icon={<Archive className="w-4 h-4" />} onClick={() => setAction("archive")} testId="action-archive">
              Archive
            </ToolbarBtn>
          )}
          {s.decision_notes && (
            <span className="ml-auto text-[11px] text-[#334155] self-center">
              Notes: <em>{s.decision_notes}</em>
            </span>
          )}
        </div>

        {action === "approve" && (
          <ApproveFlow submission={s} onClose={() => setAction(null)} onDone={() => { setAction(null); onChanged?.(); onClose(); }} />
        )}
        {action === "reject" && (
          <RejectFlow submission={s} onClose={() => setAction(null)} onDone={() => { setAction(null); onChanged?.(); onClose(); }} />
        )}
        {action === "request-info" && (
          <RequestInfoFlow submission={s} onClose={() => setAction(null)} onDone={() => { setAction(null); onChanged?.(); onClose(); }} />
        )}
        {action === "archive" && (
          <ArchiveFlow submission={s} onClose={() => setAction(null)} onDone={() => { setAction(null); onChanged?.(); onClose(); }} />
        )}

        <div className="px-6 py-4 space-y-5 text-sm text-[#0F172A]">
          <FieldGroup title="Organization">
            <Row k="Name"     v={s.organization_name} />
            <Row k="Website"  v={s.website && <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline inline-flex items-center gap-1">{s.website} <ExternalLink className="w-3 h-3" /></a>} />
            <Row k="Main phone" v={s.main_phone} />
            <Row k="Main email" v={s.main_email} />
            <Row k="Physical address" v={s.physical_address} />
            <Row k="Mailing address" v={s.mailing_address} />
            <Row k="Counties served" v={(s.counties_served || []).join(", ")} />
            <Row k="Service area" v={s.service_area} />
            <Row k="Hours" v={s.hours_of_operation} />
            <Row k="After-hours" v={s.after_hours_availability} />
            <Row k="Languages" v={(s.languages_available || []).join(", ")} />
          </FieldGroup>

          <FieldGroup title="Primary contact">
            <Row k="Name" v={`${s.primary_contact_name}${s.primary_contact_title ? ` · ${s.primary_contact_title}` : ""}`} />
            <Row k="Email" v={s.primary_contact_email} />
            <Row k="Phone" v={s.primary_contact_phone} />
          </FieldGroup>

          {(s.referral_contact_name || s.referral_contact_email) && (
            <FieldGroup title="Referral contact">
              <Row k="Name / Dept" v={s.referral_contact_name} />
              <Row k="Email" v={s.referral_contact_email} />
              <Row k="Phone" v={s.referral_contact_phone} />
            </FieldGroup>
          )}

          <FieldGroup title="Preferred referral methods">
            <Row k="Methods" v={(s.preferred_referral_methods || []).join(", ") || "—"} />
            {s.preferred_referral_method_other && <Row k="Other" v={s.preferred_referral_method_other} />}
          </FieldGroup>

          <FieldGroup title="Service profile">
            <div className="text-xs text-[#334155] mb-2">Categories</div>
            <div className="flex flex-wrap gap-1 mb-3">
              {(s.selected_categories || []).map((c, i) => (
                <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] border ${c.slug ? "bg-[#DBEAFE] text-[#1E3A8A] border-[#BFDBFE]" : "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"}`}>
                  {c.label}{c.slug ? <span className="font-mono opacity-70"> · {c.slug}</span> : <span> · unmapped</span>}
                </span>
              ))}
            </div>
            {s.other_category && <Row k="Other category" v={s.other_category} />}
            <Row k="Description"             v={s.description} multiline />
            <Row k="Eligibility"             v={s.eligibility_requirements} multiline />
            <Row k="Populations served"      v={s.populations_served} />
            <Row k="Age requirements"        v={s.age_requirements} />
            <Row k="Cost / payment"          v={s.cost_payment_info} />
            <Row k="Insurance accepted"      v={s.insurance_accepted} />
            <Row k="Required documents"      v={s.required_documents} />
            <Row k="Referral requirements"   v={s.referral_requirements} />
            <Row k="Estimated wait time"     v={s.estimated_wait_time} />
          </FieldGroup>

          <FieldGroup title="Partnership agreement">
            {Object.entries(s.mou || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <ShieldCheck className={`w-3.5 h-3.5 ${v ? "text-[#166534]" : "text-[#991B1B]"}`} />
                <span className="font-mono text-[#334155]">{k}</span>
                <span className={`font-semibold ${v ? "text-[#166534]" : "text-[#991B1B]"}`}>{v ? "acknowledged" : "missing"}</span>
              </div>
            ))}
          </FieldGroup>

          <FieldGroup title="Electronic signature">
            <Row k="Signed by"   v={`${s.signature?.name} · ${s.signature?.title}`} />
            <Row k="Typed name"  v={s.signature?.signature_text} />
            <Row k="Signed at"   v={s.signature?.signed_at ? new Date(s.signature.signed_at).toLocaleString() : ""} />
          </FieldGroup>

          {s.resulting_resource_id && (
            <div className="rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] p-3 text-xs text-[#065F46]">
              ✓ Approved &amp; created Resource <span className="font-mono">{s.resulting_resource_id}</span>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToolbarBtn({ icon, children, onClick, testId, tone = "neutral" }) {
  const styles = tone === "primary"
    ? "bg-[#1E3A8A] text-white border-[#1E3A8A] hover:bg-[#172554]"
    : tone === "danger"
      ? "bg-white text-[#991B1B] border-[#FCA5A5] hover:bg-[#FEF2F2]"
      : "bg-white text-[#0F172A] border-[#E5E5E0] hover:bg-[#F1F5F9]";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${styles}`}
    >
      {icon}{children}
    </button>
  );
}

function FlowShell({ title, tone, children, footer, testId }) {
  const toneCss = tone === "danger" ? "bg-[#FEF2F2] border-[#FCA5A5]" : tone === "warn" ? "bg-[#FFFBEB] border-[#FDE68A]" : "bg-[#F1F5F9] border-[#BFDBFE]";
  return (
    <div className={`mx-6 my-4 border rounded-2xl ${toneCss}`} data-testid={testId}>
      <div className="px-4 py-2 border-b border-current/10 font-display text-sm font-semibold text-[#0F172A]">{title}</div>
      <div className="p-4 space-y-3">{children}</div>
      <div className="px-4 py-3 border-t border-current/10 bg-white/60 rounded-b-2xl flex items-center justify-end gap-2">{footer}</div>
    </div>
  );
}

function ApproveFlow({ submission: s, onClose, onDone }) {
  // Pre-fill the create-Resource form from the submission. Admin picks
  // the canonical category (defaulting to the first matched slug if any)
  // and primary county before pushing live.
  const matchedSlugs = (s.selected_categories || []).map((c) => c.slug).filter(Boolean);
  const defaultSlug = matchedSlugs[0] || "";
  const counties = s.counties_served || [];
  const [form, setForm] = useState({
    name: s.organization_name || "",
    category: defaultSlug,
    county: counties[0] || "",
    description: s.description || "",
    eligibility: s.eligibility_requirements || "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.category) return toast.error("Choose a canonical category.");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/partner-submissions/${s.id}/approve`, form);
      // Surface the approval email status so admins know if the partner
      // was actually notified — emails can fail silently otherwise.
      const em = data.approval_email || {};
      if (em.status === "sent") {
        toast.success(`Created resource · partner notified at ${em.to}`);
      } else if (em.status === "failed") {
        toast.error(`Resource created, but the approval email FAILED: ${em.error || "see Integrations → Email logs"}`);
      } else if (em.status === "no_contact_email") {
        toast.success("Resource created — partner had no contact email, so no notification was sent.");
      } else {
        toast.success(`Created resource · ${data.resource_id}`);
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };
  return (
    <FlowShell title="Approve & create resource" tone="info" testId="approve-flow"
      footer={<>
        <button onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white">Cancel</button>
        <button onClick={submit} disabled={saving} data-testid="approve-flow-submit" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Approve &amp; create
        </button>
      </>}>
      <p className="text-xs text-[#334155]">
        Review the pre-filled Resource record below, then approve. The submission will be marked <strong>approved</strong> and the new Resource will appear in the directory immediately.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FlowField label="Resource name *" value={form.name} onChange={(v) => setField("name", v)} testId="approve-name" />
        <label className="block">
          <span className="text-xs font-medium text-[#334155]">Canonical category *</span>
          <select
            value={form.category}
            onChange={(e) => setField("category", e.target.value)}
            data-testid="approve-category"
            className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
          >
            <option value="">— pick one —</option>
            {RESOURCE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          {matchedSlugs.length > 0 && (
            <div className="mt-1 text-[10px] text-[#334155]">
              Suggested from submission: {matchedSlugs.map((m) => <button key={m} type="button" onClick={() => setField("category", m)} className="mx-0.5 underline">{m}</button>)}
            </div>
          )}
        </label>
        <label className="block">
          <span className="text-xs font-medium text-[#334155]">Primary county</span>
          <select
            value={form.county}
            onChange={(e) => setField("county", e.target.value)}
            data-testid="approve-county"
            className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
          >
            <option value="">—</option>
            {counties.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <FlowField label="Description"   value={form.description} onChange={(v) => setField("description", v)} testId="approve-description" />
        <FlowField label="Eligibility"   value={form.eligibility} onChange={(v) => setField("eligibility", v)} testId="approve-eligibility" />
        <FlowField label="Internal notes (optional)" value={form.notes} onChange={(v) => setField("notes", v)} testId="approve-notes" className="md:col-span-2" />
      </div>
    </FlowShell>
  );
}

function RejectFlow({ submission: s, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(Boolean(s.primary_contact_email));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Please write a reason.");
    setSaving(true);
    try {
      await api.post(`/admin/partner-submissions/${s.id}/reject`, { reason, notify_contact: notify });
      toast.success("Submission rejected.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };
  return (
    <FlowShell title="Reject submission" tone="danger" testId="reject-flow"
      footer={<>
        <button onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white">Cancel</button>
        <button onClick={submit} disabled={saving} data-testid="reject-flow-submit" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold bg-[#991B1B] text-white disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
          Reject
        </button>
      </>}>
      <FlowField label="Reason (stored in decision_notes) *" value={reason} onChange={setReason} testId="reject-reason" multiline />
      {s.primary_contact_email && (
        <label className="flex items-center gap-2 text-xs text-[#0F172A]">
          <input
            type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
            data-testid="reject-notify-contact"
            className="w-4 h-4 accent-[#1E3A8A]"
          />
          Email this reason to {s.primary_contact_email}
        </label>
      )}
    </FlowShell>
  );
}

function RequestInfoFlow({ submission: s, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!message.trim()) return toast.error("Write a message to the contact.");
    setSaving(true);
    try {
      await api.post(`/admin/partner-submissions/${s.id}/request-info`, { message });
      toast.success(`Sent · ${s.primary_contact_email}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };
  return (
    <FlowShell title="Request more information" tone="warn" testId="request-info-flow"
      footer={<>
        <button onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white">Cancel</button>
        <button onClick={submit} disabled={saving} data-testid="request-info-flow-submit" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
          Send &amp; mark needs-info
        </button>
      </>}>
      <p className="text-xs text-[#334155]">
        Email goes to <strong>{s.primary_contact_email}</strong>. Submission moves to <em>needs_more_info</em>.
      </p>
      <FlowField label="Message to the contact *" value={message} onChange={setMessage} testId="request-info-message" multiline />
    </FlowShell>
  );
}

function ArchiveFlow({ submission: s, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/partner-submissions/${s.id}/archive`);
      toast.success("Archived.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };
  return (
    <FlowShell title="Archive submission" tone="info" testId="archive-flow"
      footer={<>
        <button onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white">Cancel</button>
        <button onClick={submit} disabled={saving} data-testid="archive-flow-submit" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
          Archive
        </button>
      </>}>
      <p className="text-xs text-[#334155]">Hides this submission from the default queue. Visible via the Archived filter chip.</p>
    </FlowShell>
  );
}

function FlowField({ label, value, onChange, testId, multiline = false, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-medium text-[#334155]">{label}</span>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm" />
      )}
    </label>
  );
}

function FieldGroup({ title, children }) {
  // Auto-hide groups whose Row children all returned null (i.e. every
  // value in the group was empty). Stops minimal submissions from
  // rendering as empty grey boxes.
  const rendered = (Array.isArray(children) ? children : [children])
    .flat(Infinity)
    .filter((c) => c !== null && c !== undefined && c !== false);
  if (rendered.length === 0) return null;
  return (
    <section>
      <h4 className="text-xs uppercase tracking-wider font-semibold text-[#1E3A8A] mb-2">{title}</h4>
      <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-3 space-y-1.5">{rendered}</div>
    </section>
  );
}

function Row({ k, v, multiline = false }) {
  if (!v && v !== 0) return null;
  return (
    <div className={`grid grid-cols-3 gap-2 text-xs ${multiline ? "items-start" : "items-center"}`}>
      <div className="text-[#334155]">{k}</div>
      <div className="col-span-2 text-[#0F172A]">{v}</div>
    </div>
  );
}
